/* Torque product page (Fulcrum + Camber) — progressive enhancement for Shopify's native product form. */
(() => {
  'use strict';

  const GLOBAL_KEY = '__torquePdp';
  const ROOT_SELECTOR = '[data-pdp-root]';
  const controllers = new WeakMap();

  if (window[GLOBAL_KEY]?.boot) {
    window[GLOBAL_KEY].boot(document);
    return;
  }

  const text = value => value == null ? '' : String(value).trim();
  const key = value => text(value).toLocaleLowerCase();
  const same = (left, right) => key(left) === key(right);
  const unique = values => [...new Set(values.filter(Boolean))];

  function queryAll(scope, selector) {
    return scope ? [...scope.querySelectorAll(selector)] : [];
  }

  function productLabel(root) {
    return text(root?.dataset?.pdpProduct) || 'Fulcrum';
  }

  function productFamily(root) {
    return key(root?.dataset?.pdpFamily) || 'fulcrum';
  }

  function parseLightboxSet(trigger) {
    const raw = trigger?.dataset?.lightboxSet;
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      const items = parsed
        .map(item => ({ src: text(item?.src), mobileSrc: text(item?.mobileSrc), alt: text(item?.alt), caption: text(item?.caption || item?.alt) }))
        .filter(item => item.src);
      return items.length ? items : null;
    } catch (error) {
      console.warn('Torque PDP: lightbox set data is invalid; showing the single image instead.', error);
      return null;
    }
  }

  function viewMap(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const entries = Object.entries(raw).map(([view, value]) => [key(view), text(value)]).filter(([view, value]) => view && value);
    return entries.length ? Object.fromEntries(entries) : null;
  }

  function parseVariants(root) {
    const productName = productLabel(root);
    const source = root.querySelector('[data-pdp-variants]');
    if (!source) {
      console.warn('Torque PDP: variant data was not found; the native product form remains available.');
      return [];
    }

    let rawVariants;
    try {
      rawVariants = JSON.parse(source.textContent || '[]');
    } catch (error) {
      console.error('Torque PDP: variant data is invalid; the native product form remains available.', error);
      return [];
    }

    if (!Array.isArray(rawVariants)) return [];

    const seen = new Set();
    return rawVariants.map(raw => {
      const options = Array.isArray(raw?.options)
        ? raw.options.map(option => text(typeof option === 'object' ? option?.value ?? option?.name : option))
        : [text(raw?.frame), text(raw?.lens)];
      const id = text(raw?.id);
      const frame = text(raw?.frame || options[0]);
      const lens = text(raw?.lens || options[1]);
      const frameKey = text(raw?.frameKey || frame);
      const lensKey = text(raw?.lensKey || lens);

      return {
        id,
        options,
        frame,
        lens,
        frameKey,
        lensKey,
        frameLabel: text(raw?.frameLabel || frame),
        lensLabel: text(raw?.lensLabel || lens),
        available: raw?.available !== false && raw?.available !== 'false',
        price: text(raw?.price),
        priceCents: raw?.priceCents == null ? null : Number(raw.priceCents),
        compareAtPrice: text(raw?.compareAtPrice),
        framePreviewUrl: text(raw?.framePreviewUrl),
        imageKey: text(raw?.imageKey),
        imageUrl: text(raw?.imageUrl),
        imageAlt: text(raw?.imageAlt || `${frame} ${productName} sunglasses with ${lens} lenses`),
        // Per-view image map: every thumb whose data-view matches a key swaps with the variant.
        images: viewMap(raw?.images),
        imageAlts: viewMap(raw?.imageAlts),
      };
    }).filter(variant => {
      if (!variant.id || !variant.frameKey || !variant.lensKey || seen.has(variant.id)) return false;
      seen.add(variant.id);
      return true;
    });
  }

  function createController(root) {
    const ui = window.TorqueContent.labels(root);
    const productName = productLabel(root);
    const family = productFamily(root);
    const copyMedia = window.matchMedia('(max-width: 720px)');
    const variants = parseVariants(root);
    const productForm = root.querySelector('[data-product-form]');
    const variantControl = root.querySelector('[data-product-form-variant-id]');

    if (!variants.length || !productForm || !variantControl) {
      console.warn('Torque PDP: enhancement prerequisites are incomplete; using the native Shopify form.');
      return null;
    }

    const cleanups = [];
    const observers = [];
    const frames = new Set();
    const listen = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };
    const nextFrame = handler => {
      const frame = window.requestAnimationFrame(() => {
        frames.delete(frame);
        handler();
      });
      frames.add(frame);
      return frame;
    };

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)') || {
      matches: false,
      addEventListener: null,
      removeEventListener: null,
    };
    const state = {
      variant: null,
      frameButton: null,
      lensButton: null,
      galleryIndex: 0,
      adding: false,
      submitSource: 'primary',
      requestController: null,
      lightboxOpen: false,
      lightboxStandalone: null,
      lightboxSet: null,
      lightboxSetIndex: 0,
      lightboxCoverPosition: '50% 0%',
      lightboxReturnFocus: null,
      lensDialogReturnFocus: null,
      stickyVisible: false,
      destroyed: false,
    };

    const frameButtons = queryAll(root, '[data-frame]');
    const lensButtons = queryAll(root, '[data-lens]');
    const primaryAtc = root.querySelector('[data-pdp-atc]');
    const stickyAtc = root.querySelector('[data-pdp-sticky-atc]');
    const cartStatus = root.querySelector('[data-cart-status]');
    const priceNodes = queryAll(root, '[data-pdp-price], [data-atc-price], [data-sticky-price]');
    const comparePriceNodes = queryAll(root, '[data-pdp-compare-price]');
    const selectionNodes = queryAll(root, '[data-pdp-selection]');
    const frameNodes = queryAll(root, '[data-pdp-frame], [data-frame-name]');
    const lensNodes = queryAll(root, '[data-pdp-lens], [data-lens-name]:not([data-lens-info-flip])');
    const availabilityNodes = queryAll(root, '[data-pdp-availability]');
    const sticky = root.querySelector('[data-pdp-sticky]');
    const stickyImage = root.querySelector('[data-pdp-sticky-image]');
    const productImages = queryAll(root, '[data-sticky-product-image]');
    const stickyVariant = root.querySelector('[data-pdp-sticky-variant]');
    const stickyPrice = root.querySelector('[data-pdp-sticky-price]');
    const mainImage = root.querySelector('[data-gallery-main-image]');
    const galleryThumbs = queryAll(root, '[data-gallery-thumb]');
    const galleryPrev = root.querySelector('[data-gallery-prev]');
    const galleryNext = root.querySelector('[data-gallery-next]');
    const lightbox = root.querySelector('[data-pdp-lightbox]');
    const lightboxImage = lightbox?.querySelector('[data-lightbox-image]');
    const lightboxStatus = lightbox?.querySelector('[data-lightbox-status]');
    const lightboxPrev = lightbox?.querySelector('[data-lightbox-prev]');
    const lightboxNext = lightbox?.querySelector('[data-lightbox-next]');
    const lightboxClose = lightbox?.querySelector('[data-lightbox-close]');

    const comboMap = new Map();
    const idMap = new Map();
    variants.forEach(variant => {
      const combo = `${key(variant.frameKey)}::${key(variant.lensKey)}`;
      if (!comboMap.has(combo)) comboMap.set(combo, variant);
      idMap.set(variant.id, variant);
    });

    const serverInitialVariant = idMap.get(text(root.dataset.initialVariantId));

    function emit(name, detail = {}) {
      const payload = {
        family,
        product: productName,
        productUrl: root.dataset.productUrl || window.location.pathname,
        variantId: state.variant?.id || null,
        frame: state.variant?.frame || null,
        lens: state.variant?.lens || null,
        available: Boolean(state.variant?.available),
        ...detail,
      };
      root.dispatchEvent(new CustomEvent(`torque:pdp:${name}`, { bubbles: true, detail: payload }));
      if (name === 'variant-change' && state.variant) {
        window.TorqueIntegrations?.publish('variant:change', { source: `pdp:${family}`, items: [{ id: state.variant.id, price: state.variant.priceCents }] });
      }
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: `torque_pdp_${name.replace(/-/g, '_')}`, ...payload });
      }
    }

    function setText(nodes, value) {
      nodes.forEach(node => {
        node.textContent = value;
      });
    }

    function setImage(image, url, alt) {
      if (!image || !url) return;
      window.TorqueContent.image(image, url, alt);
    }

    function setCartStatus(message = '', type = '') {
      if (!cartStatus) return;
      cartStatus.textContent = message;
      cartStatus.dataset.status = type;
      cartStatus.classList.toggle('is-error', type === 'error');
      cartStatus.classList.toggle('is-pending', type === 'pending');
      cartStatus.classList.toggle('is-success', type === 'success');
      cartStatus.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
    }

    function buttonCandidates(button, kind) {
      if (!button) return [];
      return unique([
        button.dataset[kind],
        button.dataset.optionValue,
        button.dataset.value,
        button.dataset.label,
        button.dataset.name,
      ]).map(key);
    }

    function variantCandidates(variant, kind) {
      if (!variant) return [];
      const index = kind === 'frame' ? 0 : 1;
      return unique([
        variant[`${kind}Key`],
        variant[kind],
        variant[`${kind}Label`],
        variant.options[index],
      ]).map(key);
    }

    function buttonMatchesVariant(button, variant, kind) {
      const wanted = new Set(variantCandidates(variant, kind));
      return buttonCandidates(button, kind).some(candidate => wanted.has(candidate));
    }

    function buttonForVariant(buttons, variant, kind) {
      return buttons.find(button => buttonMatchesVariant(button, variant, kind)) || null;
    }

    function setButtonGroup(buttons, activeButton) {
      buttons.forEach(button => {
        const active = button === activeButton;
        button.classList.toggle('is-active', active);
        button.classList.toggle('on', active);
        button.setAttribute('aria-pressed', String(active));
        if (button.getAttribute('role') === 'radio') button.setAttribute('aria-checked', String(active));
      });
    }

    function resolveSelection(frameButton, lensButton) {
      if (!frameButton || !lensButton) return null;
      const direct = comboMap.get(`${key(frameButton.dataset.frame)}::${key(lensButton.dataset.lens)}`);
      if (direct) return direct;
      return variants.find(variant => (
        buttonMatchesVariant(frameButton, variant, 'frame')
        && buttonMatchesVariant(lensButton, variant, 'lens')
      )) || null;
    }

    function buttonDisplay(button, kind) {
      return text(button?.dataset.label || button?.dataset.name || button?.dataset.optionValue || button?.dataset[kind] || button?.textContent);
    }

    function currentSelectionText(variant = state.variant) {
      if (variant) return `${variant.frameLabel} · ${variant.lensLabel}`;
      return `${buttonDisplay(state.frameButton, 'frame')} · ${buttonDisplay(state.lensButton, 'lens')}`;
    }

    function labelTarget(button) {
      if (!button) return null;
      const nested = button.querySelector('[data-pdp-atc-label], [data-atc-label], [data-sticky-label], [data-button-label]');
      if (nested) return nested;
      return button.childElementCount === 0 ? button : null;
    }

    function setAtcLabel(button, mode) {
      if (!button) return;
      const target = labelTarget(button);
      const labels = {
        available: ui('available', button.dataset.labelAvailable || 'Add to Cart'),
        adding: ui('adding', button.dataset.labelAdding || 'Adding…'),
        unavailable: ui('unavailable', button.dataset.labelUnavailable || 'Sold Out'),
      };
      const label = labels[mode];
      button.dataset.state = mode;
      button.removeAttribute('aria-label');
      if (target) target.textContent = label;
    }

    function syncAtc() {
      const available = Boolean(state.variant?.available);
      unique([primaryAtc, stickyAtc]).forEach(button => {
        const disabled = state.adding || !available;
        if ('disabled' in button) button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
        button.setAttribute('aria-busy', String(state.adding));
        setAtcLabel(button, state.adding ? 'adding' : available ? 'available' : 'unavailable');
      });
      root.setAttribute('aria-busy', String(state.adding));
    }
    listen(copyMedia, 'change', syncAtc);

    function variantThumbIndex(variant = state.variant) {
      if (!galleryThumbs.length) return -1;
      if (variant?.imageKey) {
        const keyed = galleryThumbs.findIndex(thumb => (
          same(thumb.dataset.imageKey, variant.imageKey)
          || same(thumb.dataset.galleryThumb, variant.imageKey)
        ));
        if (keyed >= 0) return keyed;
      }
      const explicit = galleryThumbs.findIndex(thumb => (
        thumb.hasAttribute('data-variant-image')
        || ['variant', 'selected', 'product'].includes(key(thumb.dataset.galleryThumb))
      ));
      return explicit >= 0 ? explicit : 0;
    }

    function thumbItem(index) {
      const thumb = galleryThumbs[index];
      if (!thumb) return null;
      const image = thumb.matches('img') ? thumb : thumb.querySelector('img');
      const viewSrc = variantViewImage(thumb);
      if (viewSrc) {
        const viewAlt = variantViewAlt(thumb) || text(thumb.dataset.imageAlt || image?.alt || thumb.getAttribute('aria-label') || `${productName} product image`);
        // The server-rendered caption describes the initial variant only.
        const viewLabel = text(thumb.getAttribute('aria-label'));
        const caption = viewLabel ? `${currentSelectionText()} · ${viewLabel}` : viewAlt;
        return { thumb, image, src: viewSrc, alt: viewAlt, caption };
      }
      const dynamic = index === variantThumbIndex();
      return {
        thumb,
        image,
        src: dynamic && state.variant?.imageUrl
          ? state.variant.imageUrl
          : text(thumb.dataset.imageUrl || thumb.dataset.src || image?.currentSrc || image?.getAttribute('src')),
        alt: dynamic && state.variant?.imageAlt
          ? state.variant.imageAlt
          : text(thumb.dataset.imageAlt || image?.alt || thumb.getAttribute('aria-label') || `${productName} product image`),
        caption: dynamic && state.variant?.imageAlt
          ? state.variant.imageAlt
          : text(thumb.dataset.caption || thumb.dataset.imageAlt || image?.alt || ''),
      };
    }

    function variantViewImage(thumb, variant = state.variant) {
      const view = key(thumb?.dataset?.view);
      return view && variant?.images ? text(variant.images[view]) : '';
    }

    function variantViewAlt(thumb, variant = state.variant) {
      const view = key(thumb?.dataset?.view);
      return view && variant?.imageAlts ? text(variant.imageAlts[view]) : '';
    }

    function syncGalleryControls() {
      const multiple = galleryThumbs.length > 1;
      unique([galleryPrev, galleryNext]).forEach(control => {
        if ('disabled' in control) control.disabled = !multiple;
        control.hidden = !multiple;
        control.setAttribute('aria-hidden', String(!multiple));
      });
      syncLightboxControls();
    }

    function syncLightboxControls() {
      const multiple = state.lightboxSet
        ? state.lightboxSet.length > 1
        : !state.lightboxStandalone && galleryThumbs.length > 1;
      unique([lightboxPrev, lightboxNext]).forEach(control => {
        if ('disabled' in control) control.disabled = !multiple;
        control.hidden = !multiple;
        control.setAttribute('aria-hidden', String(!multiple));
      });
    }

    function syncLightboxCoverMode(trigger) {
      const isCover = Boolean(state.lightboxSet);
      lightbox?.classList.toggle('is-cover', isCover);
      if (!lightboxImage) return;
      if (isCover) {
        if (trigger) {
          const image = trigger.querySelector('img');
          state.lightboxCoverPosition = image?.style.objectPosition || image?.dataset?.tqFocal || '50% 0%';
        }
        lightboxImage.style.objectPosition = state.lightboxCoverPosition;
      } else {
        lightboxImage.style.objectPosition = '';
      }
    }

    function renderLightboxItem() {
      if (!state.lightboxOpen) return;
      let item = null;
      if (state.lightboxSet) {
        const count = state.lightboxSet.length;
        const index = ((state.lightboxSetIndex % count) + count) % count;
        state.lightboxSetIndex = index;
        const entry = state.lightboxSet[index];
        item = {
          src: copyMedia.matches && entry.mobileSrc ? entry.mobileSrc : entry.src,
          alt: entry.alt,
          caption: count > 1 ? `${entry.caption || entry.alt} · ${index + 1}/${count}` : entry.caption || entry.alt,
        };
      } else {
        item = state.lightboxStandalone || thumbItem(state.galleryIndex);
      }
      if (!item) return;
      setImage(lightboxImage, item.src, item.alt);
      if (lightboxStatus) lightboxStatus.textContent = item.alt || item.caption || '';
    }

    function renderGallery(index, source = 'gallery', announce = true) {
      if (!galleryThumbs.length) {
        setImage(mainImage, state.variant?.imageUrl, state.variant?.imageAlt);
        renderLightboxItem();
        return;
      }

      const normalized = ((Number(index) || 0) % galleryThumbs.length + galleryThumbs.length) % galleryThumbs.length;
      const item = thumbItem(normalized);
      if (!item) return;
      state.galleryIndex = normalized;
      galleryThumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === normalized;
        thumb.classList.toggle('is-active', active);
        thumb.classList.toggle('on', active);
        thumb.setAttribute('aria-pressed', String(active));
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (source === 'previous' || source === 'next' || source === 'swipe') {
        galleryThumbs[normalized].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
      setImage(mainImage, item.src, item.alt);
      renderLightboxItem();
      if (announce) emit('gallery-change', { source, galleryIndex: normalized, imageAlt: item.alt });
    }

    function refreshGalleryForVariant() {
      if (!state.variant) return;
      const dynamicIndex = variantThumbIndex(state.variant);
      if (dynamicIndex >= 0) {
        const thumb = galleryThumbs[dynamicIndex];
        const image = thumb.matches('img') ? thumb : thumb.querySelector('img');
        setImage(image, state.variant.imageUrl, state.variant.imageAlt);
      }
      let keepView = false;
      if (state.variant.images) {
        galleryThumbs.forEach(thumb => {
          const src = variantViewImage(thumb);
          if (!src) return;
          const image = thumb.matches('img') ? thumb : thumb.querySelector('img');
          setImage(image, src, variantViewAlt(thumb) || text(thumb.dataset.imageAlt || image?.alt || ''));
          thumb.dataset.imageUrl = src;
          if (galleryThumbs.indexOf(thumb) === state.galleryIndex) keepView = true;
        });
      }
      if (galleryThumbs.length) renderGallery(keepView ? state.galleryIndex : dynamicIndex >= 0 ? dynamicIndex : 0, 'variant', false);
      else setImage(mainImage, state.variant.imageUrl, state.variant.imageAlt);
      setImage(stickyImage, state.variant.imageUrl, state.variant.imageAlt);
      productImages.forEach(image => setImage(image, state.variant.imageUrl, state.variant.imageAlt));
    }

    function syncVariantUi({ resetGallery = true } = {}) {
      const variant = state.variant;
      const available = Boolean(variant?.available);
      const selection = currentSelectionText(variant);
      const frameLabel = variant?.frameLabel || buttonDisplay(state.frameButton, 'frame');
      const lensLabel = variant?.lensLabel || buttonDisplay(state.lensButton, 'lens');

      root.dataset.selectedVariantId = variant?.id || '';
      root.dataset.selectedFrame = variant?.frameKey || text(state.frameButton?.dataset.frame);
      root.dataset.selectedLens = variant?.lensKey || text(state.lensButton?.dataset.lens);
      root.dataset.variantAvailable = String(available);
      const previousVariantId = variantControl.value;
      variantControl.value = variant?.id || '';
      frameButtons.forEach(button => {
        const previewVariant = variants.find(item => buttonMatchesVariant(button, item, 'frame') && buttonMatchesVariant(state.lensButton, item, 'lens'));
        if (previewVariant?.framePreviewUrl) setImage(button.querySelector('img'), previewVariant.framePreviewUrl, '');
      });
      setButtonGroup(frameButtons, state.frameButton);
      setButtonGroup(lensButtons, state.lensButton);
      setText(frameNodes, frameLabel);
      setText(lensNodes, lensLabel);
      setText(selectionNodes, selection);
      availabilityNodes.forEach(node => {
        const availableWrap = node.querySelector('[data-pdp-shipline-available]');
        const unavailableWrap = node.querySelector('[data-pdp-shipline-unavailable]');
        if (availableWrap && unavailableWrap) {
          availableWrap.hidden = !available;
          unavailableWrap.hidden = available;
          node.classList.toggle('is-unavailable', !available);
          return;
        }
        node.textContent = available
          ? node.dataset.pdpAvailableText
          : node.dataset.pdpUnavailableText;
      });
      setText(priceNodes, variant?.price || '');
      setText(stickyVariant ? [stickyVariant] : [], selection);
      setText(stickyPrice ? [stickyPrice] : [], variant?.price || '');

      comparePriceNodes.forEach(node => {
        const visible = Boolean(variant?.compareAtPrice && !same(variant.compareAtPrice, variant.price));
        node.textContent = visible ? variant.compareAtPrice : '';
        node.hidden = !visible;
        node.setAttribute('aria-hidden', String(!visible));
      });

      syncAtc();
      if (resetGallery && variant) refreshGalleryForVariant();
      // Shopify payment terms listen to the native variant field.
      if (variantControl.value !== previousVariantId) {
        variantControl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    function replaceVariantUrl(variant) {
      if (!variant || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ ...(window.history.state || {}), variantId: variant.id }, '', url);
    }

    function applyVariant(variant, { source = 'selection', updateHistory = false, resetGallery = true } = {}) {
      if (!variant) return false;
      state.variant = variant;
      state.frameButton = buttonForVariant(frameButtons, variant, 'frame');
      state.lensButton = buttonForVariant(lensButtons, variant, 'lens');
      syncVariantUi({ resetGallery });
      if (updateHistory) replaceVariantUrl(variant);
      if (source !== 'initialization' && source !== 'history') setCartStatus('');
      emit('variant-change', { source });
      return true;
    }

    function applyIncompleteSelection(source) {
      state.variant = null;
      syncVariantUi({ resetGallery: false });
      setCartStatus(ui('combination_unavailable'), 'error');
      emit('variant-change', { source, reason: 'invalid-combination' });
    }

    function chooseOption(kind, button, source) {
      if (state.adding) return;
      if (kind === 'frame') state.frameButton = button;
      else state.lensButton = button;
      const variant = resolveSelection(state.frameButton, state.lensButton);
      if (variant) applyVariant(variant, { source, updateHistory: true });
      else applyIncompleteSelection(source);
    }

    function addOptionListeners(buttons, kind) {
      buttons.forEach((button, index) => {
        listen(button, 'click', () => chooseOption(kind, button, `${kind}-selector`));
        listen(button, 'keydown', event => {
          let nextIndex = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % buttons.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = buttons.length - 1;
          if (nextIndex == null) return;
          event.preventDefault();
          buttons[nextIndex].focus();
          chooseOption(kind, buttons[nextIndex], `${kind}-keyboard`);
        });
      });
    }

    addOptionListeners(frameButtons, 'frame');
    addOptionListeners(lensButtons, 'lens');

    galleryThumbs.forEach((thumb, index) => {
      listen(thumb, 'click', () => renderGallery(index, 'thumbnail'));
      if (!/^(BUTTON|A)$/.test(thumb.tagName)) {
        if (!thumb.hasAttribute('tabindex')) thumb.tabIndex = 0;
        if (!thumb.hasAttribute('role')) thumb.setAttribute('role', 'button');
        listen(thumb, 'keydown', event => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          renderGallery(index, 'thumbnail-keyboard');
        });
      }
    });
    listen(galleryPrev, 'click', () => renderGallery(state.galleryIndex - 1, 'previous'));
    listen(galleryNext, 'click', () => renderGallery(state.galleryIndex + 1, 'next'));

    let suppressLightboxUntil = 0;
    function addSwipeListeners(target, onSwipe) {
      let touchStartX = 0;
      let touchStartY = 0;
      listen(target, 'touchstart', event => {
        const touch = event.changedTouches?.[0];
        if (!touch) return;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      }, { passive: true });
      listen(target, 'touchend', event => {
        const touch = event.changedTouches?.[0];
        if (!touch) return;
        const deltaX = touch.clientX - touchStartX;
        const deltaY = touch.clientY - touchStartY;
        if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.25) return;
        onSwipe(deltaX < 0 ? 1 : -1);
      }, { passive: true });
    }

    const swipeTarget = mainImage?.closest('.pstage-frame') || mainImage?.parentElement || mainImage;
    addSwipeListeners(swipeTarget, delta => {
      suppressLightboxUntil = Date.now() + 450;
      renderGallery(state.galleryIndex + delta, 'swipe');
    });
    addSwipeListeners(lightbox, delta => stepLightbox(delta));

    function lightboxIsOpen() {
      return Boolean(lightbox?.open || state.lightboxOpen);
    }

    function focusableInLightbox() {
      return queryAll(lightbox, 'button:not([disabled]):not([hidden]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
        .filter(element => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true');
    }

    function openLightbox(trigger, index = state.galleryIndex, event) {
      if (!lightbox || !lightboxImage || Date.now() < suppressLightboxUntil) return;
      const galleryThumb = trigger?.closest?.('[data-gallery-thumb]');
      const standaloneImage = trigger?.querySelector?.('img');
      const isStandaloneTrigger = !galleryThumb && trigger !== mainTrigger;
      state.lightboxSet = isStandaloneTrigger ? parseLightboxSet(trigger) : null;
      state.lightboxSetIndex = 0;
      const standaloneSrc = isStandaloneTrigger
        ? text(trigger?.dataset?.imageUrl || standaloneImage?.currentSrc)
        : '';
      if (state.lightboxSet && standaloneSrc) {
        state.lightboxSetIndex = Math.max(0, state.lightboxSet.findIndex(item => item.src === standaloneSrc));
      }
      const isModelCard = Boolean(trigger?.classList?.contains('ucard') || trigger?.closest?.('.undercard'));
      state.lightboxStandalone = standaloneSrc ? {
        src: standaloneSrc,
        alt: text(standaloneImage?.alt || trigger.dataset.imageAlt || trigger.getAttribute('aria-label') || `${productName} product image`),
        caption: isModelCard ? '' : text(trigger.dataset.caption || trigger.dataset.imageAlt || ''),
      } : null;
      state.galleryIndex = galleryThumbs.length
        ? ((index % galleryThumbs.length) + galleryThumbs.length) % galleryThumbs.length
        : 0;
      state.lightboxReturnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
      state.lightboxOpen = true;
      lightbox.toggleAttribute('data-pointer-open', event?.detail > 0 || event?.pointerType === 'touch');
      syncLightboxCoverMode(trigger);
      syncLightboxControls();
      renderLightboxItem();
      if (typeof lightbox.showModal === 'function') {
        if (!lightbox.open) lightbox.showModal();
      } else {
        lightbox.hidden = false;
        lightbox.setAttribute('open', '');
        lightbox.setAttribute('role', 'dialog');
        lightbox.setAttribute('aria-modal', 'true');
      }
      document.documentElement.classList.add('pdp-lightbox-open');
      (lightboxClose || focusableInLightbox()[0] || lightbox).focus?.({ preventScroll: true });
      emit('lightbox-open', { galleryIndex: state.galleryIndex });
    }

    function finishLightboxClose() {
      if (!state.lightboxOpen) return;
      state.lightboxOpen = false;
      lightbox?.removeAttribute('data-pointer-open');
      lightbox?.removeAttribute('open');
      if (lightbox && typeof lightbox.showModal !== 'function') lightbox.hidden = true;
      document.documentElement.classList.remove('pdp-lightbox-open');
      const returnFocus = state.lightboxReturnFocus;
      state.lightboxReturnFocus = null;
      state.lightboxStandalone = null;
      state.lightboxSet = null;
      state.lightboxSetIndex = 0;
      state.lightboxCoverPosition = '50% 0%';
      lightbox?.classList.remove('is-cover');
      if (lightboxImage) lightboxImage.style.objectPosition = '';
      syncLightboxControls();
      returnFocus?.focus?.({ preventScroll: true });
      emit('lightbox-close', { galleryIndex: state.galleryIndex });
    }

    function closeLightbox() {
      if (!lightbox || !lightboxIsOpen()) return;
      if (typeof lightbox.close === 'function' && lightbox.open) lightbox.close();
      else finishLightboxClose();
    }

    function stepLightbox(delta) {
      if (state.lightboxSet) {
        if (state.lightboxSet.length < 2) return;
        state.lightboxSetIndex += delta;
        renderLightboxItem();
        emit('gallery-change', { source: 'lightbox-set', setIndex: state.lightboxSetIndex });
        return;
      }
      if (state.lightboxStandalone || !galleryThumbs.length) return;
      renderGallery(state.galleryIndex + delta, 'lightbox');
      renderLightboxItem();
    }

    const mainTrigger = mainImage?.closest('[data-lightbox-open], button, [role="button"]') || mainImage;
    const lightboxOpeners = unique([mainTrigger, ...queryAll(root, '[data-lightbox-open]')]);
    lightboxOpeners.forEach(opener => {
      listen(opener, 'click', event => {
        const thumb = opener.closest?.('[data-gallery-thumb]');
        const index = thumb ? galleryThumbs.indexOf(thumb) : state.galleryIndex;
        openLightbox(opener, index >= 0 ? index : state.galleryIndex, event);
      });
      if (!/^(BUTTON|A)$/.test(opener.tagName)) {
        if (!opener.hasAttribute('tabindex')) opener.tabIndex = 0;
        if (!opener.hasAttribute('role')) opener.setAttribute('role', 'button');
        listen(opener, 'keydown', event => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          openLightbox(opener);
        });
      }
    });
    listen(lightboxPrev, 'click', () => stepLightbox(-1));
    listen(lightboxNext, 'click', () => stepLightbox(1));
    listen(lightboxClose, 'click', closeLightbox);
    listen(lightbox, 'close', finishLightboxClose);
    listen(lightbox, 'cancel', event => {
      event.preventDefault();
      closeLightbox();
    });
    listen(lightbox, 'click', event => {
      if (event.target === lightbox) closeLightbox();
    });
    listen(lightbox, 'keydown', event => {
      if (!lightboxIsOpen()) return;
      lightbox.removeAttribute('data-pointer-open');
      if (event.key === 'Escape') {
        event.preventDefault();
        closeLightbox();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepLightbox(-1);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepLightbox(1);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableInLightbox();
      if (!focusable.length) {
        event.preventDefault();
        lightbox.focus?.();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !lightbox.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // ---- Lens chooser dialog (ported from the homepage builder) ----
    const lensDialog = root.querySelector('[data-lens-info-dialog]');
    const lensDialogOpeners = queryAll(root, '[data-lens-info-open]');
    const lensDialogClose = queryAll(lensDialog, '[data-lens-info-close]');
    const lensChoices = queryAll(lensDialog, '[data-lens-info-choice]');
    const lensCards = queryAll(lensDialog, '[data-lens-info-card]');
    const lensFlipButtons = queryAll(lensDialog, '[data-lens-info-flip]');
    const lensDialogBody = lensDialog?.querySelector('.lens-info-body');
    const lensSummary = lensDialog?.querySelector('[data-lens-info-summary]');
    const lensApply = lensDialog?.querySelector('[data-lens-info-apply]');

    function lensButtonForKey(lensKey) {
      if (!lensKey) return null;
      return lensButtons.find(button => same(button.dataset.lens, lensKey)) || null;
    }

    function lensButtonAvailable(button) {
      return Boolean(button && !state.adding && !button.disabled
        && button.getAttribute('aria-disabled') !== 'true'
        && resolveSelection(state.frameButton, button)?.available);
    }

    function setLensFlip(button, flipped) {
      const preview = button.closest('[data-lens-info-card]')?.querySelector('[data-lens-info-preview]');
      if (!preview) return;
      preview.classList.toggle('is-flipped', flipped);
      button.setAttribute('aria-pressed', String(flipped));
      const label = flipped ? button.dataset.viewFront : button.dataset.viewBase;
      const labelNode = button.querySelector('[data-lens-info-flip-label]');
      if (labelNode) labelNode.textContent = label;
      button.setAttribute('aria-label', `${button.dataset.lensName}: ${label}`);
    }

    function resetLensDialogPreview() {
      lensFlipButtons.forEach(button => setLensFlip(button, false));
      if (lensDialogBody) lensDialogBody.scrollTop = 0;
    }

    function syncLensDialogSelection() {
      const chosen = lensChoices.find(choice => choice.checked && !choice.disabled
        && lensButtonAvailable(lensButtonForKey(choice.value)));
      const chosenKey = key(chosen?.value);
      lensCards.forEach(card => {
        const active = Boolean(chosenKey) && same(card.dataset.lensInfoCard, chosenKey);
        card.classList.toggle('is-selected', active);
        const badge = card.querySelector('[data-lens-info-selected]');
        if (badge) badge.hidden = !active;
      });
      if (lensSummary) {
        const button = lensButtonForKey(chosenKey);
        lensSummary.textContent = button ? buttonDisplay(button, 'lens') : '';
      }
      if (lensApply) {
        lensApply.disabled = !chosenKey;
        lensApply.setAttribute('aria-disabled', String(!chosenKey));
      }
    }

    function openLensDialog(trigger) {
      if (!lensDialog || lensDialog.open) return;
      resetLensDialogPreview();
      const currentKey = key(state.lensButton?.dataset.lens || state.variant?.lensKey);
      lensChoices.forEach(choice => {
        const button = lensButtonForKey(choice.value);
        choice.disabled = !lensButtonAvailable(button);
        choice.checked = !choice.disabled && Boolean(currentKey) && same(choice.value, currentKey);
        const source = button?.querySelector('img');
        const card = choice.closest('[data-lens-info-card]');
        const image = card?.querySelector('img');
        if (source && image) {
          const lensSource = source.currentSrc || source.src;
          image.src = lensSource;
          card.querySelector('.lens-info-thumbnail')?.style.setProperty('--lens-outline', `url("${lensSource}")`);
        }
      });
      syncLensDialogSelection();
      state.lensDialogReturnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
      if (typeof lensDialog.showModal === 'function') {
        if (!lensDialog.open) lensDialog.showModal();
      } else {
        lensDialog.setAttribute('open', '');
        lensDialog.setAttribute('role', 'dialog');
        lensDialog.setAttribute('aria-modal', 'true');
      }
      if (lensDialogBody) lensDialogBody.scrollTop = 0;
      emit('lens-dialog-open');
    }

    function closeLensDialog() {
      if (!lensDialog) return;
      if (typeof lensDialog.close === 'function' && lensDialog.open) lensDialog.close();
      else if (lensDialog.hasAttribute('open')) {
        lensDialog.removeAttribute('open');
        finishLensDialogClose();
      }
    }

    function finishLensDialogClose() {
      resetLensDialogPreview();
      const returnFocus = state.lensDialogReturnFocus;
      state.lensDialogReturnFocus = null;
      returnFocus?.focus?.({ preventScroll: true });
      emit('lens-dialog-close');
    }

    lensDialogOpeners.forEach(opener => listen(opener, 'click', event => {
      event.preventDefault();
      openLensDialog(opener);
    }));
    lensDialogClose.forEach(button => listen(button, 'click', closeLensDialog));
    lensFlipButtons.forEach(button => listen(button, 'click', event => {
      event.stopPropagation();
      setLensFlip(button, button.getAttribute('aria-pressed') !== 'true');
    }));
    listen(lensDialog, 'close', finishLensDialogClose);
    listen(lensDialog, 'cancel', event => {
      event.preventDefault();
      closeLensDialog();
    });
    listen(lensDialog, 'click', event => {
      if (event.target !== lensDialog) return;
      const bounds = lensDialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right
        || event.clientY < bounds.top || event.clientY > bounds.bottom) closeLensDialog();
    });
    lensChoices.forEach(choice => listen(choice, 'change', syncLensDialogSelection));
    listen(lensApply, 'click', () => {
      if (!lensDialog?.open) return;
      const chosen = lensChoices.find(choice => choice.checked && !choice.disabled);
      const button = lensButtonForKey(key(chosen?.value));
      if (!lensButtonAvailable(button)) return;
      closeLensDialog();
      chooseOption('lens', button, 'lens-dialog');
    });

    function setAdding(adding) {
      state.adding = adding;
      unique([...frameButtons, ...lensButtons]).forEach(button => {
        button.disabled = adding;
        button.setAttribute('aria-disabled', String(adding));
      });
      syncAtc();
    }

    function ensureJsonEndpoint(url, fallback) {
      const value = text(url || fallback);
      if (!value) return '';
      return value.endsWith('.js') ? value : `${value.replace(/\/$/, '')}.js`;
    }

    function errorMessage(payload, response) {
      if (typeof payload?.description === 'string') return payload.description;
      if (typeof payload?.message === 'string') return payload.message;
      if (typeof payload?.errors === 'string') return payload.errors;
      if (payload?.errors && typeof payload.errors === 'object') {
        return Object.values(payload.errors).flat().map(text).filter(Boolean).join(' ');
      }
      return response?.status ? `Shopify returned ${response.status}.` : ui('cart_error');
    }

    function cartCountNodes() {
      const selector = text(root.dataset.cartCountSelector);
      const nodes = [document.getElementById('cartn'), ...queryAll(document, '[data-cart-count]')];
      if (selector) {
        try {
          nodes.push(...document.querySelectorAll(selector));
        } catch (error) {
          console.warn('Torque PDP: cart count selector is invalid.', error);
        }
      }
      return unique(nodes);
    }

    async function refreshCartCount(item) {
      const endpoint = ensureJsonEndpoint(root.dataset.cartJsUrl || root.dataset.cartUrl, '/cart');
      let cart = null;
      if (endpoint) {
        try {
          const response = await window.fetch(endpoint, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          if (response.ok) cart = await response.json();
        } catch (error) {
          console.warn('Torque PDP: the item was added, but cart count refresh failed.', error);
        }
      }
      if (Number.isFinite(Number(cart?.item_count))) {
        cartCountNodes().forEach(node => {
          node.textContent = String(cart.item_count);
          node.dataset.cartCount = String(cart.item_count);
          node.hidden = false;
        });
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, item } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart, item } }));
      return cart;
    }

    async function submitProduct(event) {
      const variant = state.variant;
      if (!variant?.available) {
        event.preventDefault();
        setCartStatus(
          ui(variant ? 'sold_out_message' : 'invalid_message'),
          'error',
        );
        emit('cart-add-error', { source: state.submitSource, reason: variant ? 'sold-out' : 'invalid-combination' });
        return;
      }

      const endpoint = ensureJsonEndpoint(root.dataset.cartAddUrl || productForm.action, '/cart/add');
      if (typeof window.fetch !== 'function' || typeof window.FormData !== 'function' || !endpoint) return;
      event.preventDefault();
      if (state.adding) return;

      variantControl.value = variant.id;
      const integrationSource = `pdp:${family}:${state.submitSource}`;
      const formData = new FormData(productForm);
      formData.set('id', variant.id);
      formData.set('quantity', '1');
      state.requestController = typeof AbortController === 'function' ? new AbortController() : null;
      setAdding(true);
      setCartStatus(ui('cart_pending'), 'pending');
      emit('cart-add-start', { source: state.submitSource, quantity: 1 });

      try {
        const response = await window.fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
          signal: state.requestController?.signal,
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(errorMessage(payload, response));
        window.TorqueIntegrations?.publish('cart:add', { source: integrationSource, requestedQuantity: 1, items: payload.items || [payload] });
        const cart = await refreshCartCount(payload);
        setCartStatus(ui('cart_success'), 'success');
        emit('cart-add-success', {
          source: state.submitSource,
          quantity: 1,
          cartItemCount: Number.isFinite(Number(cart?.item_count)) ? Number(cart.item_count) : null,
        });
      } catch (error) {
        if (error?.name === 'AbortError' && state.destroyed) return;
        const message = text(error?.message) || ui('cart_error');
        window.TorqueIntegrations?.publish('cart:error', { source: integrationSource, operation: 'add', reason: 'request-failed' });
        setCartStatus(`${ui('cart_error_prefix')} ${message} ${ui('cart_error_suffix')}`, 'error');
        emit('cart-add-error', { source: state.submitSource, reason: 'request-failed', message });
      } finally {
        state.requestController = null;
        setAdding(false);
        state.submitSource = 'primary';
      }
    }

    listen(productForm, 'submit', submitProduct);
    listen(primaryAtc, 'click', () => {
      state.submitSource = 'primary';
      emit('cta-click', { source: 'primary' });
    });
    listen(stickyAtc, 'click', () => {
      state.submitSource = 'sticky';
      emit('cta-click', { source: 'sticky' });
      if (typeof productForm.requestSubmit === 'function') productForm.requestSubmit();
      else {
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        if (productForm.dispatchEvent(submitEvent)) productForm.submit();
      }
    });

    listen(variantControl, 'change', () => {
      const variant = idMap.get(text(variantControl.value));
      if (variant && variant.id !== state.variant?.id) {
        applyVariant(variant, { source: 'native-selector', updateHistory: true });
      }
    });

    function syncStickyVisibility() {
      if (!sticky) return;
      const anchor = primaryAtc || productForm;
      const footer = root.querySelector('footer') || document.querySelector('footer');
      const anchorAbove = anchor.getBoundingClientRect().bottom <= 0;
      const footerClear = !footer || footer.getBoundingClientRect().top > window.innerHeight + 8;
      const visible = anchorAbove && footerClear;
      state.stickyVisible = visible;
      sticky.hidden = !visible;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      if ('inert' in sticky) sticky.inert = !visible;
    }

    let stickyFrame = 0;
    const scheduleSticky = () => {
      if (stickyFrame) return;
      stickyFrame = nextFrame(() => {
        stickyFrame = 0;
        syncStickyVisibility();
      });
    };
    listen(window, 'scroll', scheduleSticky, { passive: true });
    listen(window, 'resize', scheduleSticky, { passive: true });

    let revealObserver = null;
    const revealNodes = queryAll(root, '.rv');
    function revealAll() {
      revealNodes.forEach(node => node.classList.add('in'));
      revealObserver?.disconnect();
      revealObserver = null;
    }
    function initReveals() {
      if (reducedMotion.matches || typeof window.IntersectionObserver !== 'function') {
        revealAll();
        return;
      }
      revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          revealObserver?.unobserve(entry.target);
        });
      }, { threshold: 0.12 });
      revealNodes.forEach(node => revealObserver.observe(node));
      observers.push(() => revealObserver?.disconnect());
    }
    initReveals();

    function syncReducedMotion() {
      root.classList.toggle('pdp-reduced-motion', reducedMotion.matches);
      root.dataset.reducedMotion = String(reducedMotion.matches);
      if (reducedMotion.matches) revealAll();
    }
    syncReducedMotion();
    if (typeof reducedMotion.addEventListener === 'function') listen(reducedMotion, 'change', syncReducedMotion);
    else if (typeof reducedMotion.addListener === 'function') {
      reducedMotion.addListener(syncReducedMotion);
      cleanups.push(() => reducedMotion.removeListener(syncReducedMotion));
    }

    syncGalleryControls();
    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    const urlVariant = idMap.get(text(urlVariantId));
    const preferredFrame = text(root.dataset.preferredFrame) || 'Black Gloss';
    const preferredVariant = variants.find(variant => (
      variant.available && same(variant.frame, preferredFrame) && same(variant.lens, 'Blue Mirror')
    ));
    const startupVariant = urlVariant || preferredVariant || variants.find(variant => variant.available) || variants[0];
    applyVariant(startupVariant, { source: urlVariant ? 'url' : 'initialization', updateHistory: false });
    syncStickyVisibility();

    listen(window, 'popstate', () => {
      const variantId = new URL(window.location.href).searchParams.get('variant');
      const variant = idMap.get(text(variantId));
      if (variant) applyVariant(variant, { source: 'history', updateHistory: false });
    });

    root.classList.add('is-enhanced');
    root.dataset.pdpInitialized = 'true';
    root.dataset.pdpReady = 'true';
    emit('ready', { variantCount: variants.length, galleryItems: galleryThumbs.length });

    return {
      destroy() {
        if (state.destroyed) return;
        state.destroyed = true;
        state.requestController?.abort();
        cleanups.splice(0).forEach(cleanup => cleanup());
        observers.splice(0).forEach(disconnect => disconnect());
        frames.forEach(frame => window.cancelAnimationFrame(frame));
        frames.clear();
        if (lightboxIsOpen()) {
          if (typeof lightbox?.close === 'function' && lightbox.open) lightbox.close();
          else finishLightboxClose();
        }
        if (lensDialog?.open) {
          if (typeof lensDialog.close === 'function') lensDialog.close();
          else lensDialog.removeAttribute('open');
        }
        document.documentElement.classList.remove('pdp-lightbox-open');
        root.classList.remove('is-enhanced', 'pdp-reduced-motion');
        delete root.dataset.pdpInitialized;
        delete root.dataset.pdpReady;
      },
    };
  }

  function init(root) {
    if (!root || controllers.has(root)) return;
    if (typeof root._torquePdpDestroy === 'function') root._torquePdpDestroy();
    const controller = createController(root);
    if (!controller) return;
    controllers.set(root, controller);
    root._torquePdpDestroy = () => {
      controller.destroy();
      controllers.delete(root);
      delete root._torquePdpDestroy;
    };
  }

  function boot(scope = document) {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...queryAll(scope, ROOT_SELECTOR));
    unique(roots).forEach(init);
  }

  function destroy(scope) {
    const roots = [];
    if (scope?.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...queryAll(scope, ROOT_SELECTOR));
    unique(roots).forEach(root => root._torquePdpDestroy?.());
  }

  const api = { boot, destroy };
  window[GLOBAL_KEY] = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
})();
